/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import type React from "react";
import { useTranslation, TranslatedString } from "@i18n";
import { useCallback, useMemo, useRef, useState, useEffect, forwardRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import { QueryBuilder, executeQuery } from "@Services/queryBuilder";
import { deleteSchedule, saveScheduleData } from "../../../actions/task";
import { spacesDataUrl, ganttListUrl } from "../../../services/dataManager";
import { convertDate } from "../../../services/luxonHelper";
import ScheduleDetailsSidebar from "./ScheduleDetailsSidebar/ScheduleDetailsSidebar";
import ToolTipComponent from "../../../cubeComponent/html/tooltip";
import ButtonComponent from "../../../cubeComponent/form/ButtonComponent";
import ModalStateDialogComponent from "../../../cubeComponent/html/modal/ModalWithState";
import { getScheduleUrl } from "@Features/projects/utils/projectUrls";

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import SlickGrid1 from "../../../cubeComponent/html/SlickGrid1";
import { typedColumns, type GridRowBase, GridClickEvent } from "../../../cubeComponent/html/SlickGrid1";
import _ from "lodash";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import axiosWrapper from "../../../services/axiosWrapper";
import { RiFileInfoLine } from "react-icons/ri";
import Text from "@CubeComponents/html/Text/Text";
import GridPageLayout from "@CubeComponents/html/GridPageLayout";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import RadioButtonComponent from "@CubeComponents/Mui_Form/RadioButton";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { PermissionFlow } from "@Features/estimation";
import { defaultModulesMapper, folderPermissions } from "@Helpers/constants";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import NoDataSvg from "@CubeComponents/html/NoData";
import NoDataScheduleSvg from "../../../assets/icons/svg/NoScheduleList.svg";
import IconComponent from "@CubeComponents/icons";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { ArrowDirection } from "@WebTypes/scheduleDomain";
import type { DeleteScheduleRequest } from "@ServerTypes/tasks/tasks.schema";
import type { ScheduleGridRef } from "../interface";

/** Axios rejection shape this file reads: only the server's `errorMessage` is consulted. */
type ApiErrorResponse = { response?: { data?: { errorMessage?: string } } };
import type { ComponentProps } from "react";
import type { SlickDataView } from "slickgrid-react";

/** Row type for schedule list data */
interface ScheduleRow extends GridRowBase {
  counter?: number;
  title?: string;
  description?: string;
  space?: { spaceName?: string };
  startDate?: number;
  endDate?: number;
  duration?: number;
  createdDate?: number;
  createdBy?: { _id?: string; fullName?: string; name?: string };
  createdByName?: string;
  spaceId?: string;
  users?: ScheduleRowUser[];
}

interface ScheduleRowUser {
  id: string;
  permissions?: number[];
}

interface ScheduleListProps {
  /** Embedded mode: hides the page chrome and the toolbar, used by the pickers. */
  showOnlyTable?: boolean;
  /** Lets an embedding picker enable its own "Link" button when exactly one row is selected. */
  linkScheduleButtonStateEnable?: (enabled: boolean) => void;
  spaceId?: ObjectIdString;
  enableCheckboxSelector?: boolean;
  /**
   * DEAD PROP: passed by `contractSchedule`'s schedule picker but read nowhere in this component.
   * Declared so that call site keeps type-checking; dropping it there would be a behaviour-neutral
   * cleanup.
   */
  contractId?: string;
}

/** The create/edit schedule form buffer. */
interface ScheduleFormData {
  _id?: ObjectIdString;
  title: string;
  description: string;
  spaceId: string;
  projectId?: string;
  inheritPermission?: boolean;
  usersRoles?: { [userId: string]: string };
}

/**
 * The selection event SlickGrid1 forwards to `onSelectedRowIdsChanged` here: the raw SlickGrid
 * click args, from which this file reads the selected row indices and the underlying rows.
 */
type ScheduleSelectionEvent = {
  detail: {
    args: {
      rows?: number[];
      grid: { selectedRows: number[]; data: { rows: ScheduleRow[] } };
    };
  };
};

const ScheduleList = forwardRef<ScheduleGridRef<ScheduleRow>, ScheduleListProps>(
  ({ showOnlyTable = false, linkScheduleButtonStateEnable, spaceId, enableCheckboxSelector = true }, ref) => {
    const { t } = useTranslation();
    const { projectData, userDetails } = useSelector((state: RootState) => state);
    const { RESOURCE_DELETE, RESOURCE_MODIFY } = folderPermissions();
    const currentUserId = userDetails._id?.toString();

    const userHasResourcePermission = (row: ScheduleRow, permissionId: number) =>
      Array.isArray(row.users) &&
      row.users.some((u) => u.id === currentUserId && (u.permissions ?? []).includes(permissionId));
    const standByRef = useRef<ScheduleGridRef<ScheduleRow> | null>(null);
    // The handle is used as an object ref throughout; a callback ref would break every
    // `dataGrid.current` read below. Erased assertion.
    const dataGrid = (ref || standByRef) as React.RefObject<ScheduleGridRef<ScheduleRow> | null>;

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { projectId } = useParams();

    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [scheduleModal, setScheduleModal] = useState(false);
    const [showPermissionsFlow, setShowPermissionsFlow] = useState(false);
    const initialFormValues = {
      title: "",
      description: "",
      spaceId: "",
      projectId,
      inheritPermission: true,
    };
    const [scheduleData, setScheduleData] = useState<ScheduleFormData>(initialFormValues);
    const [selectedGanttId, setSelectedGanttId] = useState("");
    const [showScheduleSideBar, setShowScheduleSideBar] = useState(false);

    const [disableDeleteSchedule, setDisableDeleteSchedule] = useState(true);
    const [disableEditSchedule, setDisableEditSchedule] = useState(true);
    const [disableShowProperties, setDisableShowProperties] = useState(true);
    const [_spacesOptions, setSpacesOptions] = useState<unknown[]>([]);

    const [saveScheduleLoader, setSaveScheduleLoader] = useState(false);
    const [deleteScheduleLoader, setDeleteScheduleLoader] = useState(false);

    const propertiesTemplate = useCallback(() => <RiFileInfoLine size={18} className="cursor-pointer" />, []);

    const toolbar = useMemo(
      () => [
        {
          name: t("Create"),
          tooltip: "Schedule Creation",
          prefixIcon: "add",
          id: "custom_create",
          disabled: false,
        },
        {
          name: <Text>{t("Delete")}</Text>,
          tooltip: "Delete",
          prefixIcon: "delete",
          id: "custom_deleteGantt",
          disabled: disableDeleteSchedule,
        },
        {
          name: <Text>{t("Edit")}</Text>,
          tooltip: "Edit Schedule",
          prefixIcon: "EditIcon",
          id: "custom_editGantt",
          disabled: disableEditSchedule,
        },
      ],
      [disableDeleteSchedule, disableEditSchedule, disableShowProperties, propertiesTemplate],
    );

    const [query] = useState({
      projectId,
      spaceId,
      IdMapping: "_id",
      params: {
        projectId,
        spaceId,
        IdMapping: "_id",
      },
    });

    const handleDeleteGantt = async () => {
      const deleteParams: DeleteScheduleRequest = {
        ganttIds: [],
      };

      if (selectedScheduleData.length >= 1) {
        const ganttData = selectedScheduleData;
        // The grid's `_id` is SlickGrid's loose row key; the endpoint wants a 24-hex id. Erased
        // assertion.
        ganttData.map((item: ScheduleRow) => {
          deleteParams.ganttIds.push(item._id as ObjectIdString);
        });

        setDeleteScheduleLoader(true);
        const response = await deleteSchedule(deleteParams);
        setDeleteScheduleLoader(false);
        if (response.data.status) {
          dispatch(
            showToastNotification({
              content: t("Selected schedule(s) has been deleted successfully"),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
          dataGrid.current!.slickGrid.setSelectedRows([]);
        } else {
          dispatch(
            showToastNotification({
              content: (response.data.errorMessage || SOMETHING_WENT_WRONG) as TranslatedString,
              type: TOAST_TYPE_ERROR,
            }),
          );
        }
        handleDeleteTaskModalClose();
        fetchSchedules();
        setDisableDeleteSchedule(true);
        setDisableEditSchedule(true);
        setDisableShowProperties(true);
      }
    };

    const handleDeleteTaskModalClose = () => {
      setShowDeleteConfirmationModal(false);
    };

    const toolbarClickHandler = (params: string) => {
      const selectedData = selectedScheduleData;

      if (params === "custom_create") {
        setScheduleModal(true);
      } else if (params === "custom_deleteGantt") {
        setShowDeleteConfirmationModal(true);
      } else if (params === "custom_editGantt") {
        // The "edit" toolbar button is only enabled with exactly one row selected, and every stored
        // schedule carries these fields. Erased assertions.
        setScheduleData({
          title: selectedData[0]!.title!,
          description: selectedData[0]!.description!,
          spaceId: selectedData[0]!.spaceId!,
          projectId,
          _id: selectedData[0]!._id as ObjectIdString,
        });
        setEditMode(true);
        setScheduleModal(true);
      } else if (params === "properties") {
        openScheduleSidebar(selectedScheduleData);
      }
    };

    const openSideBarWithArrowNavigation = (condition?: ArrowDirection) => {
      const dataSource = dataGrid.current!.dataView.filteredItems;
      let gantId: string = null!;
      dataSource.map((item: ScheduleRow, index: number) => {
        if (item._id == selectedGanttId && condition == "forward") {
          if (index == dataSource.length - 1) index = 0;
          else index = index + 1;

          gantId = dataGrid.current!.dataView.filteredItems[index]!._id as string;
          setSelectedGanttId(gantId);
          dataGrid.current!.dataView.setSelectedIds([gantId]);
        } else if (item._id == selectedGanttId && condition == "backward") {
          if (index == 0) index = dataSource.length - 1;
          else index = index - 1;

          gantId = dataGrid.current!.dataView.filteredItems[index]!._id as string;
          setSelectedGanttId(gantId);
          dataGrid.current!.dataView.setSelectedIds([gantId]);
        }
      });
    };

    const [selectedScheduleData, setSelectedScheduleData] = useState<ScheduleRow[]>([]);

    // SlickGrid1 types this callback's argument as an opaque event bag; the fields read below are
    // what the grid really passes. Erased assertions at each read.
    const recordClick = (
      args: Parameters<NonNullable<ComponentProps<typeof SlickGrid1>["onSelectedRowIdsChanged"]>>[0],
    ) => {
      if (!dataGrid.current) return;

      let selectedRowData: ScheduleRow[] | undefined;
      const totalRowsSelected = (args as ScheduleSelectionEvent).detail.args.grid.selectedRows.length;

      if ((args as ScheduleSelectionEvent).detail.args.rows) {
        selectedRowData = (args as ScheduleSelectionEvent).detail.args.rows!.map(
          (rowIndex: number) => (args as ScheduleSelectionEvent).detail.args.grid.data.rows[rowIndex]!,
        );
      }

      if (linkScheduleButtonStateEnable) {
        linkScheduleButtonStateEnable(totalRowsSelected == 1);
      }

      const canModifySelected =
        Array.isArray(selectedRowData) &&
        selectedRowData.length > 0 &&
        selectedRowData.every((row) => userHasResourcePermission(row, RESOURCE_MODIFY.id));
      const canDeleteSelected =
        Array.isArray(selectedRowData) &&
        selectedRowData.length > 0 &&
        selectedRowData.some((row) => userHasResourcePermission(row, RESOURCE_DELETE.id));

      if (totalRowsSelected === 1) {
        setDisableShowProperties(false);
        setDisableEditSchedule(!canModifySelected);
        setDisableDeleteSchedule(!canDeleteSelected);
      } else if (totalRowsSelected > 1) {
        setShowScheduleSideBar(false);
        setDisableShowProperties(true);
        setDisableEditSchedule(true);
        setDisableDeleteSchedule(!canDeleteSelected);
      } else {
        setShowScheduleSideBar(false);
        setDisableEditSchedule(true);
        setDisableShowProperties(true);
        setDisableDeleteSchedule(true);
      }

      // KNOWN BUG, preserved verbatim: a selection event without `rows` leaves this `undefined`, which
      // then lands in a state declared as an array. Erased assertion.
      setSelectedScheduleData(selectedRowData!);
    };

    const formatCreatedDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ScheduleRow,
    ) => {
      const dateFormat = convertDate(Number(dataContext?.createdDate), "dateForTooltip");
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <div style={{ width: "fit-content" }}>
          <ToolTipComponent showTipPointer={false} position="bottom" content={dateFormat}>
            <span>{convertDate(Number(dataContext?.createdDate), "dateForText")}</span>
          </ToolTipComponent>
        </div>,
      );
      return container;
    };

    const formatStartDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ScheduleRow,
    ) => {
      if (!dataContext?.startDate) return "-";
      const container = document.createElement("div");
      const dateFormat = convertDate(Number(dataContext.startDate), "dateForTooltip");
      const root = createRoot(container);
      root.render(
        <div style={{ width: "fit-content" }}>
          <ToolTipComponent showTipPointer={false} position="bottom" content={dateFormat}>
            <span>{convertDate(Number(dataContext.startDate), "dateForText")}</span>
          </ToolTipComponent>
        </div>,
      );
      return container;
    };

    const formatEndDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ScheduleRow,
    ) => {
      if (!dataContext?.endDate) return "-";

      const dateFormat = convertDate(Number(dataContext.endDate), "dateForTooltip");
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <div style={{ width: "fit-content" }}>
          <ToolTipComponent showTipPointer={false} position="bottom" content={dateFormat}>
            <span>{convertDate(Number(dataContext.endDate), "dateForText")}</span>
          </ToolTipComponent>
        </div>,
      );
      return container;
    };

    const formatDuration = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: ScheduleRow,
    ) => {
      if (!dataContext?.duration) return "-";
      return `${dataContext.duration} days`;
    };

    const saveSchedule = async () => {
      setSaveScheduleLoader(true);
      try {
        if (scheduleData.inheritPermission || showPermissionsFlow || editMode) {
          const data = {
            ...scheduleData,
            title: scheduleData.title.trim(),
            description: scheduleData.description.trim(),
          };
          // SHAPE GAP: the form buffer holds plain strings until save; /schedule/saveScheduleData wants
          // 24-hex ids for `_id`/`spaceId`/`projectId`. Erased assertion.
          const res = await saveScheduleData(
            data as typeof data & { spaceId: ObjectIdString; projectId?: ObjectIdString },
          );
          if (res.data.status) {
            if (data._id) {
              dispatch(
                showToastNotification({
                  content: t("edit_schedule", { scheduleName: data.title }),
                  type: TOAST_TYPE_SUCCESS,
                }),
              );
            } else {
              dispatch(
                showToastNotification({
                  content: t("create_schedule", { scheduleName: data.title }),
                  type: TOAST_TYPE_SUCCESS,
                }),
              );
            }
            fetchSchedules();
            closeScheduleModal();
          } else {
            dispatch(
              showToastNotification({
                content: (res.data.errorMessage || SOMETHING_WENT_WRONG) as TranslatedString,
                type: TOAST_TYPE_ERROR,
              }),
            );
            closeScheduleModal();
          }
        } else {
          setShowPermissionsFlow(true);
        }
      } catch (error) {
        dispatch(
          showToastNotification({
            content: ((error as ApiErrorResponse)?.response?.data?.errorMessage ||
              SOMETHING_WENT_WRONG) as TranslatedString,
            type: TOAST_TYPE_ERROR,
          }),
        );
        closeScheduleModal();
      } finally {
        setSaveScheduleLoader(false);
      }
    };

    const addedByTemplate = (
      _row: number,
      _cell: number,
      _value: unknown,
      _columnDef: unknown,
      dataContext: ScheduleRow,
    ) => {
      if (!dataContext) return;
      let _id;
      if (dataContext.createdBy) {
        _id = dataContext?.createdBy._id;
      } else {
        _id = "asdasdas";
      }
      const container = document.createElement("div");
      renderUserTag(container, { id: _id });
      return container;
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setScheduleData({
        ...scheduleData,
        [event.target.name]: event.target.value,
      });
    };
    const _handleDropDownChange = (_event: unknown, newValue: { id?: string; _id?: string } | null) => {
      if (newValue) {
        setScheduleData({ ...scheduleData, spaceId: newValue.id || newValue._id! });
      }
    };

    const handlePermission = () => {
      setScheduleData((prev: ScheduleFormData) => ({
        ...prev,
        inheritPermission: !prev.inheritPermission,
      }));
    };

    const getBtnText = () => {
      if (editMode) {
        return t("Save");
      } else if (scheduleData.inheritPermission || showPermissionsFlow) {
        return t("Create");
      } else {
        return t("Next");
      }
    };

    const usersCallBack = (users: NonNullable<ScheduleFormData["usersRoles"]>) => {
      setScheduleData((prev: ScheduleFormData) => ({
        ...prev,
        usersRoles: users,
      }));
    };

    const createScheduleContent = () => {
      if (showPermissionsFlow) {
        return (
          <PermissionFlow
            module={defaultModulesMapper.SCHEDULES}
            spaceId={scheduleData.spaceId}
            getUsersCallBack={usersCallBack}
          />
        );
      }
      return (
        <div className="flex flex-col gap-6">
          <div className="flex items-start gap-4">
            <div className="w-[7.5rem] flex items-center gap-1">
              <Text className="font-semibold">{t("Name")}</Text>
              <span className="text-red-500">*</span>
            </div>
            <div className="flex-1">
              <TextFieldComponent
                name="title"
                value={scheduleData.title}
                onChange={handleInputChange}
                variant="outlined"
                placeholder="Schedule Name"
              />
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-[7.5rem]">
              <Text className="font-semibold">{t("Description")}</Text>
            </div>
            <div className="flex-1">
              <textarea
                className="mb-[0.4rem] w-full border border-[#19204880] px-[0.5rem] py-[0.42rem] outline-none focus:border-[#0078d4]"
                rows={5}
                minLength={1}
                maxLength={255}
                name="description"
                placeholder={t("Enter Description")}
                value={scheduleData.description}
                onChange={handleInputChange}
              />
            </div>
          </div>
          {!editMode && (
            <div className="flex items-start gap-4">
              <div className="w-[7.5rem]">
                <Text className="font-semibold">{t("Permissions")}</Text>
              </div>
              <div className="flex flex-1 flex-col gap-3">
                <RadioButtonComponent
                  change={handlePermission}
                  checked={scheduleData.inheritPermission}
                  label={t("Inherit permissions from parent folder")}
                />
                <RadioButtonComponent
                  change={handlePermission}
                  checked={!scheduleData.inheritPermission}
                  label={t("Specify permissions in the next step")}
                />
              </div>
            </div>
          )}
        </div>
      );
    };
    const createScheduleModal = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        footerBorderTop
        footerContent={
          <div className="flex gap-4">
            <ButtonComponent
              onClick={saveSchedule}
              loading={saveScheduleLoader}
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              disabled={scheduleData.spaceId == "" || scheduleData.title?.trim() == ""}
            >
              <Text>{getBtnText()}</Text>
            </ButtonComponent>
            <ButtonComponent
              buttonType={BUTTON_TYPES.BORDER_BUTTON}
              onClick={showPermissionsFlow ? () => setShowPermissionsFlow(false) : closeScheduleModal}
            >
              <Text>{showPermissionsFlow ? t("Back") : t("Discard")}</Text>
            </ButtonComponent>
          </div>
        }
        headerTitle={
          <div className="flex flex-row py-1 w-full justify-between items-center">
            <div className="flex items-center gap-2">
              {showPermissionsFlow && (
                <IconComponent
                  name="BackwardArrow"
                  className="cursor-pointer me-2"
                  onClick={() => setShowPermissionsFlow(false)}
                  width="1.25rem"
                  height="1.25rem"
                />
              )}
              <Text className="text-2xl font-semibold">
                {editMode
                  ? t("Edit Schedule")
                  : showPermissionsFlow
                    ? t("Specify Permission")
                    : t("Create New Schedule")}
              </Text>
            </div>
            {!showPermissionsFlow && (
              <SelectSpaces
                moduleName={defaultModulesMapper.SCHEDULES}
                projectId={projectId}
                isDisabled={editMode}
                selectedSpaceCallback={(params: { _id?: string } | null) => {
                  setScheduleData((prev: ScheduleFormData) => ({
                    ...prev,
                    spaceId: editMode ? prev.spaceId : (params?._id ?? ""),
                  }));
                }}
              />
            )}
          </div>
        }
        isVisible
        onCancel={closeScheduleModal}
        modelContent={createScheduleContent()}
        width="45%"
      />
    );

    const closeScheduleModal = () => {
      setScheduleData(initialFormValues);
      setScheduleModal(false);
      setEditMode(false);
      setShowPermissionsFlow(false);
      setSaveScheduleLoader(false);
    };

    const created = (e: GridClickEvent) => {
      if (dataGrid?.current && !showOnlyTable) {
        const target = e.detail.eventData.target;

        if (
          !(
            target.classList.contains("slick-group-toggle") ||
            target.classList.contains("slick-cell-checkboxsel") ||
            target.closest(".slick-cell-checkboxsel")
          )
        ) {
          navigate(
            getScheduleUrl({
              projectId,
              // `SlickGrid.data` is the DataView, whose filtered rows are protected on the vendor class.
              // Erased assertions.
              scheduleId: (
                e.detail.args.grid as typeof e.detail.args.grid & {
                  data: SlickDataView<ScheduleRow> & { filteredItems: ScheduleRow[] };
                }
              ).data.filteredItems[e.detail.args.row]!._id as string,
              selectedTab: "Gantt",
            }),
          );
        }
      }
    };

    const openScheduleSidebar = (args: ScheduleRow[]) => {
      if (Array.isArray(args) && args.length > 0 && args[0]?._id) {
        setSelectedGanttId(args[0]!._id as string);
      }
      setShowScheduleSideBar((prev) => !prev);
    };

    const columnDefinitions = useMemo(
      () =>
        typedColumns<ScheduleRow>([
          {
            field: "counter",
            name: t("ID"),
            id: "counter",
            isPrimaryKey: true,
            filterType: "Text",
            maxWidth: 150,
          },
          {
            field: "title",
            name: t("Title"),
            id: "title",
            filterType: "Text",
            wrapText: true,
          },
          {
            field: "description",
            name: t("Description"),
            id: "description",
            filterType: "Text",
            wrapText: true,
          },
          {
            field: "space",
            name: t("Space Name"),
            id: "space.spaceName",
            _valueGetter: (row) => row.space?.spaceName,
            filterType: "Text",
            formatter: (_row, _cell, _value, _col, dataContext) => dataContext.space?.spaceName,
            maxWidth: 130,
          },
          {
            field: "startDate",
            name: t("Start Date"),
            id: "startDate",
            filterType: "Date",
            formatter: formatStartDate,
            _valueGetter: (row) => (row.startDate ? convertDate(Number(row.startDate), "dateForTooltip") : "-"),
          },
          {
            field: "endDate",
            name: t("End Date"),
            id: "endDate",
            filterType: "Date",
            formatter: formatEndDate,
            _valueGetter: (row) => (row.endDate ? convertDate(Number(row.endDate), "dateForTooltip") : "-"),
          },
          {
            field: "duration",
            id: "duration",
            name: t("Duration"),
            filterType: "Number",
            formatter: formatDuration,
            _excelValueGetter: (row) => (row.duration ? `${row.duration} days` : "-"),
          },
          {
            field: "createdDate",
            name: t("Created On"),
            id: "createdDate",
            filterType: "Date",
            formatter: formatCreatedDate,
            _valueGetter: (row) => (row.createdDate ? convertDate(Number(row.createdDate), "dateForTooltip") : ""),
          },
          {
            field: "createdByName",
            name: t("Created by"),
            sortable: true,
            id: "createdByName",
            filterType: "Text",
            formatter: addedByTemplate,
            _valueGetter: (row: ScheduleRow) => row.createdBy?.fullName || row.createdBy?.name || "",
            _excelValueGetter: (row: ScheduleRow) => row.createdBy?.fullName || row.createdBy?.name || "",
          },
        ]),
      [],
    );

    useEffect(() => {
      document.title = `Schedules - ${projectData?.description}`;
    }, [projectData?.description]);

    // Fetch spaces options
    useEffect(() => {
      const fetchSpacesOptions = async () => {
        try {
          const spacesQuery = new QueryBuilder().addParams("projectId", projectId);
          const result = (await executeQuery(spacesDataUrl, spacesQuery)) as { result?: unknown[] };
          setSpacesOptions(result.result || []);
        } catch (e) {
          console.error("Error fetching spaces options:", e);
          Promise.reject(e);
        }
      };
      if (projectId) {
        fetchSpacesOptions();
      }
    }, [projectId]);

    // SCHEMA GAP: the schedule list is served through the generic query endpoint, whose response has
    // no declared schema; `result` is the array of schedule rows.
    const processData = (data: { data: { result?: ScheduleRow[] } }) => {
      const items: ScheduleRow[] = data.data.result ?? [];

      return items.map((item: ScheduleRow) => ({
        ...item,
        counter: `SCH- ${String(item.counter).padStart(4, "0")}`,
        createdByName: (item.createdBy?.fullName || item.createdBy?.name || "").toLowerCase(),
      }));
    };

    const [scheduleDataset, setScheduleDataset] = useState<ScheduleRow[]>([]);

    const fetchSchedules = useCallback(async () => {
      const response = await axiosWrapper.post(ganttListUrl, {
        value: {
          ...query,
          skip: 0,
          take: 10000,
          requiresCounts: false,
        },
      });
      const processed = processData(response as { data: { result?: ScheduleRow[] } });
      // The list rows are re-shaped for display (`counter` becomes the padded "SCH-NNNN" label).
      // Erased assertion.
      setScheduleDataset(processed as typeof processed & ScheduleRow[]);
    }, [projectId, spaceId, query]);

    useEffect(() => {
      if (projectId) fetchSchedules();
    }, [projectId, spaceId, fetchSchedules]);

    const getGridComponent = () => (
      <SlickGrid1
        dropdownColumn={2}
        ref={dataGrid}
        toolbar={showOnlyTable ? [] : toolbar}
        toolbarClickHandler={toolbarClickHandler}
        onSelectedRowIdsChanged={recordClick}
        dataset={scheduleDataset}
        idMapping="_id"
        initialSortColumnId="createdDate"
        onClick={created}
        gridOptions={{
          forceFitColumns: true,
          enableSorting: true,
        }}
        selectCheckBoxOnRowClick={!enableCheckboxSelector}
        selectAtmostOneCheckBoxOnRowClick={!enableCheckboxSelector}
        disableCheckBoxSelection={!enableCheckboxSelector}
        columnDefinitions={columnDefinitions}
        enableSortingOption
        enableFilteringOption
        enableGroupingOption
        enableColumnChooser
        excelExport
        propertiesPanel
      />
    );

    const noScheduleData = () => (
      <div className="flex justify-center h-full w-full items-center p-12 rounded-lg">
        <div className="flex flex-col justify-center items-center gap-5">
          <NoDataSvg svg={NoDataScheduleSvg} text1={t("No Schedule has been added yet")} />
          <ButtonComponent
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            onClick={() => toolbarClickHandler("custom_create")}
          >
            <div className="flex items-center gap-x-2">
              <IconComponent name="add" width="1rem" height="1rem" />
              <Text>{t("Create")}</Text>
            </div>
          </ButtonComponent>
        </div>
      </div>
    );

    const hasInsufficientDeletePermission =
      Array.isArray(selectedScheduleData) &&
      selectedScheduleData.some((row: ScheduleRow) => !userHasResourcePermission(row, RESOURCE_DELETE.id));

    const deleteModalContent = () => (
      <div className="flex flex-col gap-4 max-w-md">
        <span>
          <Text>{t("Are you sure you want to delete the selected Schedule(s)?")}</Text>
        </span>
        {hasInsufficientDeletePermission && (
          <div className="flex items-center gap-2 rounded-lg p-4 border border-gradient_color bg-communication_Completion_Lighter">
            <Text noWrap>
              {t(
                "Some of the selected Schedule(s) will not be deleted because you do not have sufficient permissions.",
              )}
            </Text>
          </div>
        )}
      </div>
    );

    const deleteConfirmDialog = () => (
      <ModalStateDialogComponent
        footerContent={
          <div className="flex gap-4">
            <ButtonComponent
              onClick={handleDeleteGantt}
              buttonType="gradientButton"
              loading={deleteScheduleLoader}
              disabled={deleteScheduleLoader}
            >
              <Text>{t("Confirm")}</Text>
            </ButtonComponent>
            <ButtonComponent
              onClick={handleDeleteTaskModalClose}
              buttonType="borderButton"
              disabled={deleteScheduleLoader}
            >
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
          </div>
        }
        onCancel={handleDeleteTaskModalClose}
        headerTitle={
          <span>
            <Text>{t("Delete Schedule(s)")}</Text>
          </span>
        }
        isVisible={showDeleteConfirmationModal}
        modelContent={deleteModalContent()}
        footerBorderTop
      />
    );

    return showOnlyTable ? (
      getGridComponent()
    ) : (
      <div className="flex h-full w-full overflow-auto">
        {scheduleModal ? createScheduleModal() : null}
        {showDeleteConfirmationModal && deleteConfirmDialog()}

        <GridPageLayout showHeader={true} title="Schedules" description="List of all the schedules in this project">
          {scheduleDataset.length > 0 ? getGridComponent() : noScheduleData()}
        </GridPageLayout>
        {showScheduleSideBar && (
          <div className="flex flex-1 min-w-[30%] max-w-[40%] mx-2">
            <ScheduleDetailsSidebar
              openSideBarWithArrowNavigation={openSideBarWithArrowNavigation}
              setScheduleSideBar={setShowScheduleSideBar}
              // The sidebar wants a 24-hex Schedule id; the grid row key is SlickGrid's loose `_id` and
              // the `Array.isArray` guard leaves `false` in the falsy branch. Erased assertion.
              ganttId={(Array.isArray(selectedScheduleData) && selectedScheduleData[0]?._id) as ObjectIdString}
              selectedCount={Array.isArray(selectedScheduleData) ? selectedScheduleData.length : 0}
            />
          </div>
        )}
      </div>
    );
  },
);

ScheduleList.displayName = "ScheduleList";
export default ScheduleList;
